/**
 * MultiViewContainer — free-form absolute positioning with drag/resize for multiple SLD diagrams.
 * Extracted from SLDViewerModal.tsx (no behavior changes).
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import type {
  DataPointConfig,
  DataPointRuntimeState,
  AdvancedAlarmRule,
  TriggeredAdvancedRule,
} from '@/types/monitoring';
import type { SLDPointMapping, SLDDiagram } from '@/stores/monitoringStore';
import { MIN_WIN_W, MIN_WIN_H, type WindowLayout } from './sldUtils';
import { SLDNewWindowPortal } from './SLDNewWindowPortal';
import { SLDStandaloneViewer } from './SLDStandaloneViewer';
import MultiViewWindow from './MultiViewWindow';

export interface MultiViewContainerProps {
  diagrams: SLDDiagram[];
  datapoints: DataPointConfig[];
  advancedRules: AdvancedAlarmRule[];
  runtimeStates: Map<string, DataPointRuntimeState>;
  triggeredAdvancedRules: TriggeredAdvancedRule[];
  onMaximize: (diagramId: string) => void;
  onPointClick: (e: React.MouseEvent, mapping: SLDPointMapping, diagram: SLDDiagram) => void;
}

export default function MultiViewContainer({
  diagrams,
  datapoints,
  advancedRules,
  runtimeStates,
  triggeredAdvancedRules,
  onMaximize,
  onPointClick,
}: MultiViewContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [layouts, setLayouts] = useState<WindowLayout[]>([]);
  const [, setTopZ] = useState(10);
  const [poppedOut, setPoppedOut] = useState<Set<string>>(new Set());

  const dragRef = useRef<{
    type: 'drag' | 'resize';
    id: string;
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    origW: number;
    origH: number;
  } | null>(null);

  // Calculate initial grid layout when diagrams change
  useEffect(() => {
    const container = containerRef.current;
    if (!container || diagrams.length === 0) { setLayouts([]); return; }

    const rect = container.getBoundingClientRect();
    const cols = Math.ceil(Math.sqrt(diagrams.length));
    const rows = Math.ceil(diagrams.length / cols);
    const pad = 12;
    const ww = Math.floor((rect.width - pad * (cols + 1)) / cols);
    const wh = Math.floor((rect.height - pad * (rows + 1)) / rows);

    setLayouts(
      diagrams.map((d, i) => ({
        id: d.id,
        x: pad + (i % cols) * (ww + pad),
        y: pad + Math.floor(i / cols) * (wh + pad),
        w: Math.max(ww, MIN_WIN_W),
        h: Math.max(wh, MIN_WIN_H),
        zIndex: i + 1,
      })),
    );
    setTopZ(diagrams.length + 1);
  }, [diagrams.length]); // only recalculate on count change

  // Bring window to front
  const bringToFront = useCallback(
    (id: string) => {
      setTopZ((z) => {
        const next = z + 1;
        setLayouts((prev) => prev.map((l) => (l.id === id ? { ...l, zIndex: next } : l)));
        return next;
      });
    },
    [],
  );

  // Drag/resize handlers via window-level listeners
  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;

      setLayouts((prev) =>
        prev.map((l) => {
          if (l.id !== d.id) return l;
          if (d.type === 'drag') {
            return { ...l, x: Math.max(0, d.origX + dx), y: Math.max(0, d.origY + dy) };
          } else {
            return { ...l, w: Math.max(MIN_WIN_W, d.origW + dx), h: Math.max(MIN_WIN_H, d.origH + dy) };
          }
        }),
      );
    };
    const onMouseUp = () => {
      dragRef.current = null;
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const startDrag = useCallback(
    (e: React.MouseEvent, id: string, type: 'drag' | 'resize') => {
      e.preventDefault();
      e.stopPropagation();
      bringToFront(id);
      const layout = layouts.find((l) => l.id === id);
      if (!layout) return;
      dragRef.current = {
        type,
        id,
        startX: e.clientX,
        startY: e.clientY,
        origX: layout.x,
        origY: layout.y,
        origW: layout.w,
        origH: layout.h,
      };
    },
    [layouts, bringToFront],
  );

  // Pop-out into separate window
  const handlePopOut = useCallback((id: string) => {
    setPoppedOut((prev) => new Set(prev).add(id));
  }, []);

  const handlePopBack = useCallback((id: string) => {
    setPoppedOut((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  if (diagrams.length === 0) {
    return (
      <div className="sld-multi-view-container">
        <div className="sld-multi-view-empty">No diagrams to display</div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="sld-multi-view-container sld-multi-view-freeform">
      {layouts.map((layout) => {
        const diagram = diagrams.find((d) => d.id === layout.id);
        if (!diagram) return null;
        if (poppedOut.has(layout.id)) {
          return (
            <SLDNewWindowPortal
              key={`portal-${layout.id}`}
              title={`SLD \u2014 ${diagram.name}`}
              diagram={diagram}
              onClose={() => handlePopBack(layout.id)}
            >
              <SLDStandaloneViewer diagram={diagram} />
            </SLDNewWindowPortal>
          );
        }
        return (
          <MultiViewWindow
            key={layout.id}
            diagram={diagram}
            layout={layout}
            datapoints={datapoints}
            advancedRules={advancedRules}
            runtimeStates={runtimeStates}
            triggeredAdvancedRules={triggeredAdvancedRules}
            onMaximize={onMaximize}
            onPopOut={handlePopOut}
            onStartDrag={startDrag}
            onBringToFront={bringToFront}
            onPointClick={onPointClick}
          />
        );
      })}
    </div>
  );
}
